import { useState, useEffect } from 'react';
import { Drawer, DrawerContent, DrawerTitle, DrawerDescription } from './ui/drawer';
import { Button } from './ui/button';
import { Star, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { REVIEW_PROMPT_DISMISS_KEY_PREFIX } from '../utils/orderStatus';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Home-screen Swiggy-style review prompt (Business Rule #19).
 *
 * Bottom sheet that surfaces a rateable order on Landing mount. Tapping a star
 * auto-submits the rating to `POST /api/orders/:id/rate`; the sheet then morphs
 * into a "Thanks!" state with `Add detailed feedback` and `Done` CTAs.
 *
 * Dismissal (×) is permanent per-order via localStorage — the Profile +
 * OrderTracking inline pills remain as backup entry points.
 *
 * @param {object} order — the rateable order to surface
 * @param {string} token — auth bearer
 * @param {boolean} isOpen — controlled open state
 * @param {(submitted: boolean) => void} onClose — called when sheet closes
 * @param {(stars: number) => void} onOpenDetail — called when user taps "Add detailed feedback"
 */
export default function ReviewPrompt({ order, token, isOpen, onClose, onOpenDetail }) {
  const [hoveredRating, setHoveredRating] = useState(0);
  const [submittedRating, setSubmittedRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setHoveredRating(0);
      setSubmittedRating(0);
      setSubmitting(false);
    }
  }, [isOpen, order?.id]);

  const persistDismissal = () => {
    if (!order?.id) return;
    try {
      localStorage.setItem(`${REVIEW_PROMPT_DISMISS_KEY_PREFIX}${order.id}`, '1');
    } catch {
      // ignore quota / private-mode failures — re-prompting once is harmless
    }
  };

  const handleSheetOpenChange = (open) => {
    if (open) return;
    persistDismissal();
    onClose(submittedRating > 0, submittedRating);
  };

  const submitStars = async (stars) => {
    if (submitting || submittedRating > 0 || !order?.id) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/orders/${order.id}/rate`,
        { rating: stars },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSubmittedRating(stars);
      persistDismissal();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not save rating');
      setHoveredRating(0);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddDetail = () => {
    if (submittedRating === 0) return;
    onOpenDetail(submittedRating);
  };

  const handleDone = () => {
    onClose(true, submittedRating);
  };

  const subtitle = formatOrderSubtitle(order);
  const displayRating = submittedRating || hoveredRating;

  return (
    <Drawer open={isOpen} onOpenChange={handleSheetOpenChange} shouldScaleBackground={false}>
      <DrawerContent
        className="pb-[calc(env(safe-area-inset-bottom)+1rem)]"
        data-testid="review-prompt-sheet"
      >
        <button
          type="button"
          onClick={() => handleSheetOpenChange(false)}
          className="absolute top-3 right-3 p-2 rounded-full text-muted-foreground hover:bg-muted transition"
          aria-label="Dismiss review prompt"
          data-testid="review-prompt-close"
        >
          <X className="w-5 h-5" strokeWidth={1.5} />
        </button>

        <div className="px-6 pt-2 pb-6 max-w-md mx-auto w-full">
          {submittedRating === 0 ? (
            <>
              <DrawerTitle className="font-serif text-xl text-center">
                How was your order?
              </DrawerTitle>
              {subtitle && (
                <DrawerDescription className="text-center mt-1">
                  {subtitle}
                </DrawerDescription>
              )}

              <div className="flex justify-center gap-2 mt-6">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => submitStars(star)}
                    onMouseEnter={() => setHoveredRating(star)}
                    onMouseLeave={() => setHoveredRating(0)}
                    disabled={submitting}
                    className="p-1 transition-transform active:scale-95 disabled:opacity-60"
                    aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
                    data-testid={`review-prompt-star-${star}`}
                  >
                    <Star
                      className={`w-12 h-12 transition-colors ${
                        star <= displayRating
                          ? 'fill-accent text-accent'
                          : 'text-muted-foreground/40'
                      }`}
                      strokeWidth={1.5}
                    />
                  </button>
                ))}
              </div>

              <p className="text-xs text-muted-foreground text-center mt-4">
                Tap a star to submit your rating.
              </p>
            </>
          ) : (
            <>
              <div className="flex justify-center">
                <div className="w-12 h-12 rounded-full bg-accent/15 flex items-center justify-center">
                  <Check className="w-6 h-6 text-accent" strokeWidth={2} />
                </div>
              </div>
              <DrawerTitle className="font-serif text-xl text-center mt-3">
                Thanks for rating!
              </DrawerTitle>
              <DrawerDescription className="text-center mt-1">
                Want to tell us more about each item?
              </DrawerDescription>

              <div className="flex justify-center gap-1 mt-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-5 h-5 ${
                      star <= submittedRating
                        ? 'fill-accent text-accent'
                        : 'text-muted-foreground/30'
                    }`}
                    strokeWidth={1.5}
                  />
                ))}
              </div>

              <div className="flex flex-col gap-2 mt-6">
                <Button
                  onClick={handleAddDetail}
                  className="w-full rounded-full"
                  data-testid="review-prompt-add-detail"
                >
                  Add detailed feedback
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDone}
                  className="w-full rounded-full"
                  data-testid="review-prompt-done"
                >
                  Done
                </Button>
              </div>
            </>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

/**
 * Human-readable order subtitle for the prompt header — branch + relative day.
 * Returns null when neither piece is available.
 */
function formatOrderSubtitle(order) {
  if (!order) return null;
  const branch = order.branch_name;
  const when = relativeDayLabel(
    order.pickup_scanned_at || order.updated_at || order.ready_at || order.created_at,
  );
  if (branch && when) return `${branch} · ${when}`;
  return branch || when || null;
}

function relativeDayLabel(iso) {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  const now = new Date();
  const sameDay =
    then.getFullYear() === now.getFullYear() &&
    then.getMonth() === now.getMonth() &&
    then.getDate() === now.getDate();
  if (sameDay) return 'today';
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (
    then.getFullYear() === yesterday.getFullYear() &&
    then.getMonth() === yesterday.getMonth() &&
    then.getDate() === yesterday.getDate()
  ) {
    return 'yesterday';
  }
  return then.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
}
